import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';

import { buildPanelShape, buildSphereShape } from '@toolproof-core/visualization/_pure/shapes';

import { PRESENTATION_DEFAULTS } from '@/workflow/_data/config/fragments/_@shared/presentationDefaults';
import { GEOMETRY_SCALE } from '@/workflow/_data/config/fragments/geometryScale';
import { ABOVE_ITEM_LABEL, FLAG_ABOVE_ITEM_LABEL } from '@/workflow/_data/config/fragments/labels';
import { RESOURCE_TYPE_REQUIREMENTS } from '@/workflow/_data/config/fragments/resourceTypes';
import type { AuthoredBaseConfig } from '@/workflow/_contract/config';
import { buildWorkflowConfig } from '@/workflow/_pure/config/presentation';


export const AUTHORED_BASE_CONFIG: AuthoredBaseConfig = {
    colorPalette: PRESENTATION_DEFAULTS.colorPalette,
    geometryScale: {
        multipliers: GEOMETRY_SCALE.multipliers,
        workflowStateEntries: GEOMETRY_SCALE.workflowStateEntries,
    },

    toolSelection: {
        toolHandles: [],
    },

    resourceTypeInputSpec: {
        requiredResourceTypes: [
            ...RESOURCE_TYPE_REQUIREMENTS,
        ],
        presetResourcesByType: {},
    },

    spaceRenderOptions: {
        backgroundColor: PRESENTATION_DEFAULTS.colors.backgroundColor,
        camera: {
            pose: {
                position: [0, 40, 120],
                target: [0, 0, 0],
                fov: 50,
            },
        },
        // Tuned so nothing fades at the default camera distance; labels thin out only as you pull back.
        labelProximity: {
            minApparentSize: 0.004,
            fadeBand: 0.5,
            hoverFontScale: 1.6,
        },
    },

    domainPresentation: {
        entities: {
            cases: {
                bands: {
                    enabled: false,
                    gutter: 0.03,
                    minWidth: 0.02,
                    opacity: 0.05,
                    gradientCutoffCenter: 0.5,
                    gradientCutoffWidth: 0.12,
                },
                tori: {
                    enabled: false,
                    opacity: 0.22,
                    whenHeightRatio: 0.65,
                    clearanceFraction: 0.02,
                    minimumClearance: 0.005,
                    cornerRoundingFraction: 0.5,
                    cornerSegments: 6,
                    segments: 192,
                },
            },
            macroSteps: {
                tori: {
                    opacity: 0.14,
                    clearanceFraction: 0.04,
                    minimumClearance: 0.005,
                    cornerRoundingFraction: 0.5,
                    cornerSegments: 6,
                    segments: 192,
                },
            },
            tools: {
                layout: {
                    layoutKind: 'sector-ring',
                    anchor: { anchorKind: 'origin' },
                    outerRadius: GEOMETRY_SCALE.rootRadius,
                    innerRadius: 0,
                    angularGapFraction: 0.16,
                    arcSegments: 24,
                    outerThickness: GEOMETRY_SCALE.rootRadius * 0.2,
                    activeThicknessScale: 0.3,
                },

                item: {
                    visualStyle: {
                        color: PRESENTATION_DEFAULTS.colors.tools.fill,
                        activeHighlightColor: PRESENTATION_DEFAULTS.colors.activation.active,
                        linkedHighlightColor: PRESENTATION_DEFAULTS.colors.activation.linked,
                    },
                    label: {
                        onItem: {
                            color: PRESENTATION_DEFAULTS.colors.tools.label,
                            orientation: {
                                orientationKind: 'fixed',
                                plane: 'flat',
                                facing: 'inward',
                                rotation: [0, 0, Math.PI / 2],
                            },
                        },
                        backItem: {
                            color: PRESENTATION_DEFAULTS.colors.tools.label,
                            fontSize: 0.6,
                            orientation: {
                                orientationKind: 'fixed',
                                plane: 'upright',
                                facing: 'outward',
                            },
                        },
                    },
                },
                labelChannelPolicy: {
                    aboveItem: 'always',
                    onItem: 'always',
                    backItem: 'always',
                },

                labelLayoutPolicy: {
                    placement: 'centered',
                    sizePolicy: 'fit-available-space',
                    minFontSize: 0.3,
                    maxFontSize: 1.1,
                    yOffset: 0.02,
                },
            },
            resourceTypes: {
                angularPlacement: {
                    placementKind: 'explicit',
                    angleByResourceTypeHandle: {
                        [CONSTANTS.Handles.ResourceTypes.TYPE_Boolean]: 5 * Math.PI / 4,
                        [CONSTANTS.Handles.ResourceTypes.TYPE_Natural]: Math.PI,
                    },
                },
                item: {
                    shape: buildSphereShape({
                        radius: GEOMETRY_SCALE.rootRadius
                            * GEOMETRY_SCALE.multipliers.resourceTypes.ringRadiusFromRoot
                            * GEOMETRY_SCALE.multipliers.resourceTypes.sphereRadiusFromRing,
                    }),
                    visualStyle: {
                        color: PRESENTATION_DEFAULTS.colors.resourceTypes.fill,
                        highlightCue: PRESENTATION_DEFAULTS.sphereHighlightCue,
                        activeHighlightColor: PRESENTATION_DEFAULTS.colors.activation.active,
                        linkedHighlightColor: PRESENTATION_DEFAULTS.colors.activation.linked,
                    },
                    label: {
                        aboveItem: {
                            ...FLAG_ABOVE_ITEM_LABEL,
                            placement: { placementKind: 'channel-default', offset: [0, 0, 0] },
                        },
                    },
                },
                layout: {
                    layoutKind: 'ring',
                    anchor: { anchorKind: 'origin' },
                        radius: GEOMETRY_SCALE.rootRadius
                            * GEOMETRY_SCALE.multipliers.resourceTypes.ringRadiusFromRoot,
                },
                ringGuide: {
                    segments: 128,
                    y: 0,
                    width: 1,
                    color: PRESENTATION_DEFAULTS.colors.resourceTypes.guide,
                    opacity: 1.0,
                },
                labelChannelPolicy: {
                    aboveItem: 'always',
                },
            },
            roles: {
                input: {
                    layout: {
                        layoutKind: 'crescent',
                        radius: GEOMETRY_SCALE.rootRadius
                            * GEOMETRY_SCALE.multipliers.resourceTypes.ringRadiusFromRoot
                            * GEOMETRY_SCALE.multipliers.resourceTypes.sphereRadiusFromRing
                            * GEOMETRY_SCALE.multipliers.roles.ringRadiusFromResourceTypeSphere,
                        arcCenterAngle: 0,
                        equatorGap: Math.PI / 4,
                        rotation: [0, 0, Math.PI / 2],
                    },
                    crescentGuide: {
                        segments: 96,
                        width: 0.5,
                        color: PRESENTATION_DEFAULTS.colors.roles.guide,
                        opacity: 1.0,
                    },
                    item: {
                        shape: buildSphereShape({
                            radius: GEOMETRY_SCALE.rootRadius
                                * GEOMETRY_SCALE.multipliers.resourceTypes.ringRadiusFromRoot
                                * GEOMETRY_SCALE.multipliers.resourceTypes.sphereRadiusFromRing
                                * GEOMETRY_SCALE.multipliers.roles.ringRadiusFromResourceTypeSphere
                                * GEOMETRY_SCALE.multipliers.roles.sphereRadiusFromRing,
                        }),
                        visualStyle: {
                            color: PRESENTATION_DEFAULTS.colors.roles.input,
                            highlightCue: PRESENTATION_DEFAULTS.sphereHighlightCue,
                            activeHighlightColor: PRESENTATION_DEFAULTS.colors.activation.active,
                            linkedHighlightColor: PRESENTATION_DEFAULTS.colors.activation.linked,
                        },
                        label: {
                            aboveItem: {
                                ...FLAG_ABOVE_ITEM_LABEL,
                                placement: { placementKind: 'channel-default', offset: [0, 0, 0] },
                            },
                        },
                    },
                    labelChannelPolicy: {
                        aboveItem: 'always',
                    },
                },
                output: {
                    layout: {
                        layoutKind: 'crescent',
                        radius: GEOMETRY_SCALE.rootRadius
                            * GEOMETRY_SCALE.multipliers.resourceTypes.ringRadiusFromRoot
                            * GEOMETRY_SCALE.multipliers.resourceTypes.sphereRadiusFromRing
                            * GEOMETRY_SCALE.multipliers.roles.ringRadiusFromResourceTypeSphere,
                        arcCenterAngle: Math.PI,
                        equatorGap: Math.PI / 4,
                        rotation: [0, 0, Math.PI / 2],
                    },
                    crescentGuide: {
                        segments: 96,
                        width: 0.5,
                        color: PRESENTATION_DEFAULTS.colors.roles.guide,
                        opacity: 1.0,
                    },
                    item: {
                        shape: buildSphereShape({
                            radius: GEOMETRY_SCALE.rootRadius
                                * GEOMETRY_SCALE.multipliers.resourceTypes.ringRadiusFromRoot
                                * GEOMETRY_SCALE.multipliers.resourceTypes.sphereRadiusFromRing
                                * GEOMETRY_SCALE.multipliers.roles.ringRadiusFromResourceTypeSphere
                                * GEOMETRY_SCALE.multipliers.roles.sphereRadiusFromRing,
                        }),
                        visualStyle: {
                            color: PRESENTATION_DEFAULTS.colors.roles.output,
                            highlightCue: PRESENTATION_DEFAULTS.sphereHighlightCue,
                            activeHighlightColor: PRESENTATION_DEFAULTS.colors.activation.active,
                            linkedHighlightColor: PRESENTATION_DEFAULTS.colors.activation.linked,
                        },
                        label: {
                            aboveItem: {
                                ...FLAG_ABOVE_ITEM_LABEL,
                                placement: { placementKind: 'channel-default', offset: [0, 0, 0] },
                            },
                        },
                    },
                    labelChannelPolicy: {
                        aboveItem: 'always',
                    },
                },
            },
            toolSteps: {
                slotBands: {
                    enabled: true,
                    variant: 'serrated',
                    width: 0.05,
                    thickness: 0.04,
                    teeth: { count: 96, depth: 0.6 },
                },
                tubeRadius: GEOMETRY_SCALE.rootRadius
                    * GEOMETRY_SCALE.multipliers.toolSteps.tubeRadiusFromRoot,
                outerRim: {
                    radialWidth: 0.16,
                    height: 1.2,
                    opacity: 0.82,
                },
                tubeRadiusMultipliersByToolStepSlot: {
                    topLevel: 1.0,
                    what: 1.0,
                    when: 1.0,
                },
                radialSegments: 10,
                tubularSegments: 96,
                labelChannelPolicy: {
                    aboveItem: 'never',
                },
                item: {
                    visualStyle: {
                        color: PRESENTATION_DEFAULTS.colors.toolSteps.fill,
                        activeHighlightColor: PRESENTATION_DEFAULTS.colors.activation.active,
                        linkedHighlightColor: PRESENTATION_DEFAULTS.colors.activation.linked,
                        opacity: 1,
                    },
                    label: {
                        aboveItem: {
                            ...ABOVE_ITEM_LABEL,
                            placement: { placementKind: 'channel-default', offset: [0, 0.45, 0] },
                        },
                    },
                },
            },
            workflowStateEntries: {
                enabled: true,
                operationPresentationMode: 'panels',
                labelChannelPolicy: {
                    aboveItem: 'hover',
                },
                item: {
                    shape: buildPanelShape({
                        width: GEOMETRY_SCALE.rootRadius
                            * GEOMETRY_SCALE.workflowStateEntries.panels.panelWidthFromRoot,
                        height: GEOMETRY_SCALE.rootRadius
                            * GEOMETRY_SCALE.workflowStateEntries.panels.panelHeightFromRoot,
                        depth: GEOMETRY_SCALE.rootRadius
                            * GEOMETRY_SCALE.workflowStateEntries.panels.panelDepthFromRoot,
                    }),
                    input: {
                        visualStyle: {
                            color: PRESENTATION_DEFAULTS.colors.workflowStateEntries.input,
                            activeHighlightColor: PRESENTATION_DEFAULTS.colors.activation.active,
                            linkedHighlightColor: PRESENTATION_DEFAULTS.colors.activation.linked,
                            opacity: 1,
                        },
                    },
                    output: {
                        visualStyle: {
                            color: PRESENTATION_DEFAULTS.colors.workflowStateEntries.output,
                            activeHighlightColor: PRESENTATION_DEFAULTS.colors.activation.active,
                            linkedHighlightColor: PRESENTATION_DEFAULTS.colors.activation.linked,
                            opacity: 1,
                        },
                    },
                    label: {
                        aboveItem: {
                            ...ABOVE_ITEM_LABEL,
                        },
                        onItem: {
                            color: PRESENTATION_DEFAULTS.colors.workflowStateEntries.label,
                            fontSize: 1.0,
                            orientation: {
                                orientationKind: 'fixed',
                                plane: 'flat',
                                facing: 'inward',
                            },
                            doubleSidedReadable: true,
                            placement: { placementKind: 'channel-default', offset: [0, 0, 0] },
                        },
                    },
                },
            },
        },
        connectors: {
            toolRole: {
                visualStyle: {
                    color: PRESENTATION_DEFAULTS.colors.connectors.default,
                    opacity: 1,
                },
                path: {
                    pathKind: 'straight',
                    curvePlane: 'auto',
                    curveStrength: 0.15,
                    curveMinOffset: 0.25,
                    curveDirection: 'auto',
                },
            },
            resourceTypeRadial: {
                enabled: true,
                visualStyle: {
                    color: PRESENTATION_DEFAULTS.colors.connectors.default,
                    opacity: 1.0,
                },
                path: {
                    pathKind: 'straight',
                    curvePlane: 'auto',
                    curveStrength: 0.15,
                    curveMinOffset: 0.25,
                    curveDirection: 'auto',
                },
            },
            workflowStateEntry: {
                enabled: true,
                visualStyle: {
                    color: PRESENTATION_DEFAULTS.colors.connectors.default,
                    opacity: 1.0,
                },
                path: {
                    pathKind: 'bracket',
                    bracketOffset: 1.0,
                    curvePlane: 'xz',
                    curveStrength: 0.15,
                    curveMinOffset: 0.25,
                    curveDirection: 'positive',
                },
            },
            macroStep: {
                casePair: {
                    enabled: true,
                    curveStrength: 0.4,
                    curveMinOffset: 0.5,
                    activeHighlightColor: 'yellow',
                },
                booleanLaneAngularOffset: Math.PI / 27,
                lowerAngle: 'branch',
                radialOffset: 0,
                visualStyle: {
                    color: PRESENTATION_DEFAULTS.colors.connectors.default,
                    opacity: 0.65,
                },
                path: {
                    pathKind: 'straight',
                    curvePlane: 'auto',
                    curveStrength: 0.15,
                    curveMinOffset: 0.25,
                    curveDirection: 'auto',
                },
            },
        },
    },
};

export const BASE_CONFIG = buildWorkflowConfig(AUTHORED_BASE_CONFIG);
